import { useNavigate } from 'react-router-dom'
import { useApp } from '../lib/store'

/**
 * Con dấu của quán đã mua gói.
 *
 * Một cái huy chương: vòng tròn, ngôi sao, hai dải ruy băng. Thứ chủ quán mua phần lớn là
 * những chuyện không nhìn thấy được — báo cáo thuế, bỏ giới hạn sản phẩm, bỏ giới hạn hoá
 * đơn mỗi ngày. Cái dấu này là chỗ nói rằng số tiền ấy có đổi lấy được cái gì.
 */
export const MedalGlyph = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <circle cx="12" cy="9.2" r="6.2" fill="none" stroke="currentColor" strokeWidth="1.9" />
    <path
      d="M12 6.1l1.3 2.6 2.9.4-2.1 2 .5 2.8-2.6-1.3-2.6 1.3.5-2.8-2.1-2 2.9-.4z"
      fill="currentColor"
    />
    <path
      d="M8.2 14.6 6.7 21.2l5.3-2.5 5.3 2.5-1.5-6.6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

/** Số ngày còn lại của gói. `null` khi quán không có gói trả phí đang chạy. */
function daysLeft(plan: string | undefined, expiresAt: string | null | undefined): number | null {
  if (!plan || plan === 'Free' || !expiresAt) return null
  const left = Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86_400_000)
  return left > 0 ? left : null
}

/** Mũi tên đi lên — lối vào trang nâng gói. */
const UpGlyph = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d="M12 19.5V5.2M5.8 11.4 12 5.2l6.2 6.2"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

/**
 * Huy hiệu gói trên thanh trên.
 *
 * Chỗ này luôn có một cái gì đó, tuỳ quán đang ở đâu:
 *
 *   · Đang có gói       — huy chương xanh, tên gói. Quán trả tiền rồi thì phải thấy được
 *                         điều đó ở chỗ họ nhìn nhiều nhất, chứ không phải đào ba lớp vào
 *                         Cài đặt mới ra. Khách đứng trước quầy cũng nhìn thấy màn hình này.
 *   · Sắp hết hạn       — vẫn huy chương ấy, nhưng ghi số ngày còn lại. Nó bắt đầu nhắc.
 *   · Chưa mua, hết hạn — nút "Nâng cấp" màu hổ phách, bấm vào là tới thẳng bảng giá.
 *
 * Không bao giờ để trống: gói miễn phí mà chỗ này trống thì người muốn trả tiền phải đi
 * tìm, còn quán đã trả thì chẳng thấy mình được gì.
 */
export function PlanBadge() {
  const nav = useNavigate()
  const shop = useApp((s) => s.shop)

  const left = daysLeft(shop?.plan, shop?.planExpiresAt)
  const go = () => nav('/settings/billing')

  if (left === null) {
    return (
      <button
        type="button"
        className="plan-badge is-free"
        onClick={go}
        title="Đang dùng gói miễn phí — bấm để xem bảng giá"
      >
        <UpGlyph />
        <b>Nâng cấp</b>
      </button>
    )
  }

  const soon = left <= 7

  /*
   * Đeo tên quán, không đeo tên gói.
   *
   * "Quán nhỏ" là cách sigoPOS gọi bậc giá, còn thứ chủ quán muốn thấy trên màn hình của
   * mình là cái tên họ đặt cho quán. Khách đứng trước quầy đọc được tên quán trên một cái
   * huy chương vàng, chứ không phải đọc xem quán đang mua bậc nào.
   *
   * Sắp hết hạn thì nhường chỗ cho con số ngày: lúc ấy nhắc việc quan trọng hơn khoe tên.
   */
  return (
    <button
      type="button"
      className={`plan-badge${soon ? ' is-soon' : ''}`}
      onClick={go}
      title={
        soon
          ? `Gói còn ${left} ngày — bấm để gia hạn`
          : `${shop?.name ?? 'Quán'} · gói Quán nhỏ, còn ${left} ngày`
      }
    >
      <MedalGlyph />
      <b>{soon ? `Còn ${left} ngày` : (shop?.name ?? 'Quán nhỏ')}</b>
    </button>
  )
}
